import { httpsCallable, type HttpsCallableResult } from 'firebase/functions'
import { functionsAU, functionsUS } from '@/lib/firebase'

export type FunctionsRegion = 'us' | 'au'

const regionFns = {
  us: functionsUS,
  au: functionsAU,
} as const

/**
 * Region-aware httpsCallable helper.
 * - `us` → us-central1 (auth, KYC, chat, biddle CRUD)
 * - `au` → australia-southeast2 (search, letters, campaign claims)
 */
export async function callCallable<TRequest, TResponse>(
  region: FunctionsRegion,
  name: string,
  data?: TRequest,
): Promise<TResponse> {
  const callable = httpsCallable<TRequest, TResponse>(regionFns[region], name)
  const result: HttpsCallableResult<TResponse> = await callable(data as TRequest)
  return result.data
}

// Thin aliases kept for later phases — do not call from Phase 0 UI yet.

export const sendWelcomeEmail = () =>
  callCallable<{ platform: 'web' }, { success?: boolean }>('us', 'sendWelcomeEmail', {
    platform: 'web',
  })

export const sendVerificationEmail = () =>
  callCallable<{ platform: 'web' }, { success?: boolean }>('us', 'sendVerificationEmail', {
    platform: 'web',
  })

export const sendPasswordResetEmail = (email: string) =>
  callCallable<{ email: string; platform: 'web' }, { success: boolean }>(
    'us',
    'sendPasswordResetEmail',
    { email, platform: 'web' },
  )

export const hardDeleteUserAccount = () =>
  callCallable<Record<string, never>, { success: boolean }>('us', 'hardDeleteUserAccount', {})

export type PropertySuggestion = {
  id: string
  fullAddress: string
  suburb?: string
  state?: string
  postCode?: string
}

type RawPropertyHit = {
  id?: string
  fullAddress?: string
  suburb?: string
  state?: string
  postCode?: string
  data?: Record<string, unknown>
}

function normalizePropertySuggestion(raw: RawPropertyHit): PropertySuggestion | null {
  const data = raw.data && typeof raw.data === 'object' ? raw.data : (raw as Record<string, unknown>)
  const id = typeof raw.id === 'string' ? raw.id : typeof data.id === 'string' ? data.id : null
  const fullAddress =
    (typeof data.fullAddress === 'string' && data.fullAddress) ||
    (typeof raw.fullAddress === 'string' && raw.fullAddress) ||
    null
  if (!id || !fullAddress) return null
  return {
    id,
    fullAddress,
    suburb: typeof data.suburb === 'string' ? data.suburb : raw.suburb,
    state: typeof data.state === 'string' ? data.state : raw.state,
    postCode:
      typeof data.postCode === 'string'
        ? data.postCode
        : typeof data.postcode === 'string'
          ? data.postcode
          : raw.postCode,
  }
}

export async function searchPropertiesByPrefix(prefix: string, limit = 8) {
  const data = await callCallable<
    { prefix: string; limit: number },
    { properties?: RawPropertyHit[] }
  >('au', 'searchPropertiesByPrefix', { prefix, limit })
  return {
    properties: (data.properties ?? [])
      .map(normalizePropertySuggestion)
      .filter((p): p is PropertySuggestion => p != null),
  }
}

export type ResolvePlaceResult =
  | { status: 'found'; property: { id: string; data: Record<string, unknown> } }
  | { status: 'not_found' }

export const resolvePlaceToProperty = (placeId: string, formattedAddress: string) =>
  callCallable<
    { placeId: string; formattedAddress: string },
    ResolvePlaceResult & { fetchedAt?: number }
  >('au', 'resolvePlaceToProperty', { placeId, formattedAddress })

export type AreaPropertyHit = {
  id: string
  data: Record<string, unknown>
}

export type AreaPinHit = {
  id: string
  lat: number
  lng: number
  label: string
  geohash_6?: string
}

/** Map-area search — classic gh8 (legacy) or gh6 pin buckets (pins). */
export const searchPropertiesInArea = (
  geohashes: string[],
  mode: 'legacy' | 'pins' = 'legacy',
) =>
  callCallable<
    { geohashes: string[]; mode: 'legacy' | 'pins' },
    {
      mode?: 'legacy' | 'pins'
      properties?: AreaPropertyHit[]
      pins?: AreaPinHit[]
      fetchedAt?: number
    }
  >('au', 'searchPropertiesInArea', { geohashes, mode })

export const resolveBiddleShortId = (shortId: string) =>
  callCallable<{ shortId: string }, { propertyID: string; biddleId: string }>(
    'au',
    'resolveBiddleShortId',
    { shortId },
  )

export type CreateBiddleResult = {
  biddleId: string
  buyerBiddleTag: string
  letterState?: string | null
  letterFailureReason?: string | null
}

/** Matches iOS `createBiddle` payload (expiry as epoch seconds). */
export const createBiddle = (payload: {
  buyerID: string
  propertyID: string
  fullAddress: string
  price: number
  settlement: string
  deposit: string
  notes: string
  expiry?: number | null
  isOpenOffer: boolean
  sendLetter?: boolean
}) => callCallable<typeof payload, CreateBiddleResult>('us', 'createBiddle', payload)

export const deleteBiddle = (biddleID: string) =>
  callCallable<{ biddleID: string }, { success: boolean; biddleId?: string }>('us', 'deleteBiddle', {
    biddleID,
  })

export const initializeOwnershipVerification = (payload: {
  propertyID: string
  userProvidedEmail: string
}) =>
  callCallable<typeof payload, { docID: string; verificationHash: string }>(
    'us',
    'initializeOwnershipVerification',
    payload,
  )

export const finalizeOwnershipVerification = (payload: { documentID: string }) =>
  callCallable<typeof payload, { success?: boolean }>('us', 'finalizeOwnershipVerification', payload)

export const submitLetterOwnershipClaim = (payload: {
  campaignCode: string
  propertyID: string
  ownerConfirmed: boolean
}) =>
  callCallable<typeof payload, { claimId: string }>('au', 'submitLetterOwnershipClaim', payload)

export const initiateConnectId = (authorisationServerId: string) =>
  callCallable<{ authorisationServerId: string }, { authUrl: string; state: string }>(
    'us',
    'initiateConnectId',
    { authorisationServerId },
  )

export const completeConnectId = (payload: { code: string; state: string; iss?: string }) =>
  callCallable<typeof payload, { success: boolean; displayName?: string }>(
    'us',
    'completeConnectId',
    payload,
  )

export const createSumsubLink = () =>
  callCallable<Record<string, never>, { permalink: string }>('us', 'createSumsubLink', {})

export type ConnectIdProvider = {
  authorisationServerId: string
  name: string
  logoUri?: string
  isFallback?: boolean
}

export const listConnectIdProviders = () =>
  callCallable<Record<string, never>, { providers: ConnectIdProvider[] }>(
    'us',
    'listConnectIdProviders',
    {},
  )

export const getStreamChatToken = () =>
  callCallable<Record<string, never>, { token: string; streamId: string }>(
    'us',
    'getStreamChatToken',
    {},
  )

/** Serializable biddle fields for Stream channel creation (dates as epoch seconds). */
export type ChatBiddlePayload = {
  id?: string
  propertyID: string
  price: number
  settlement: string
  deposit: string
  notes: string
  expiry?: number | null
  isOpenOffer: boolean
  timeCreated?: number
  ownerHasViewed?: boolean
  ownerHasAccepted?: boolean
  buyerBiddleTag?: string
  ownerBiddleTag?: string | null
}

export const createBuyerOwnerChat = (payload: { biddleID: string; biddle: ChatBiddlePayload }) =>
  callCallable<typeof payload, { success: boolean; channelId: string; chatHash?: string }>(
    'us',
    'createBuyerOwnerChat',
    payload,
  )

export type NameRevealAction =
  | 'request'
  | 'accept'
  | 'decline'
  | 'accept_and_request'
  | 'counter_request'
  | 'share_name'

export type NameRevealResult = {
  success: boolean
  message: string
  newState: {
    nameVisibilityState: string
    buyerRequestedOwnerName: string
    ownerRequestedBuyerName: string
  }
}

/** `channelCID` is the Firestore chat hash (no `chat_` prefix). */
export const handleNameRevealRequest = (payload: {
  channelCID: string
  action: NameRevealAction
  userRole: 'buyer' | 'owner'
}) => callCallable<typeof payload, NameRevealResult>('us', 'handleNameRevealRequest', payload)

export const purchaseLetterPack = (payload: { packSku: string; paymentMethodId: string }) =>
  callCallable<typeof payload, { letterCredits: number; addedCredits: number }>(
    'au',
    'purchaseLetterPack',
    payload,
  )

export const createLetterPackPaymentIntent = (packSku: string) =>
  callCallable<{ packSku: string }, { clientSecret: string; paymentIntentId: string }>(
    'au',
    'createLetterPackPaymentIntent',
    { packSku },
  )

export const cancelLetter = (biddleId: string) =>
  callCallable<{ biddleId: string }, { success: boolean }>('au', 'cancelLetter', { biddleId })

export const sendLetterForExistingBiddle = (biddleId: string) =>
  callCallable<{ biddleId: string }, { success: boolean }>('au', 'sendLetterForExistingBiddle', {
    biddleId,
  })
